import React from 'react';
import DataTable from './DataTable.jsx';
import MoneyCell from './MoneyCell.jsx';
import { fmtMoney, fmtPct, buildComparison, buildAllTimeStats } from '../engine.js';

/* ComparisonView — renders period-over-period deltas for a forecast array.
   Props:
     forecast     — array from buildForecast/buildThreeStatementProjection
     mode         — 'none' | 'mom' | 'qoq' | 'yoy' | 'hybrid' | 'all'
     metricKeys   — which columns to compare (default: key financial metrics)
     metricLabels — display labels for each metric
*/

const DEFAULT_METRICS = [
  { key: 'liquidEnding', label: 'Liquid EoM' },
  { key: 'ccDebtEnding', label: 'Short-term Debt EoM' },
  { key: 'netFCF',       label: 'Net FCF' },
  { key: 'fvfMRR',       label: 'Recurring revenue' },
];

function DeltaCell({ cell, tone = 'auto' }) {
  const base = 'inline-flex items-baseline gap-1 tabular-nums text-[11.5px]';
  if (!cell || cell.delta == null) return <span className={`${base} text-text-muted italic`}>—</span>;
  /* Direction verdict mirrors SparklineDeltaBadge rules — tone-invariant,
     purely directional on the raw delta, flat = grey sideways arrow. */
  const sign = cell.delta > 0 ? 'up' : cell.delta < 0 ? 'dn' : 'flat';
  const arrow = sign === 'up' ? '▲' : sign === 'dn' ? '▼' : '→';
  const toneCls = sign === 'up' ? 'text-accent-green'
                : sign === 'dn' ? 'text-accent-red'
                : 'text-text-dim';
  return (
    <span className={`${base} ${toneCls}`}>
      <span className="text-[10px]">{arrow}</span>
      <span>{fmtMoney(Math.abs(cell.delta), { decimals: 2 })}</span>
      {cell.pct != null && <span className="text-[10px] text-text-muted">({fmtPct(Math.abs(cell.pct))})</span>}
    </span>
  );
}

export default function ComparisonView({
  forecast, mode = 'none',
  metrics = DEFAULT_METRICS,
  title = 'Period comparison',
}) {
  if (mode === 'none' || !Array.isArray(forecast) || forecast.length === 0) return null;

  const metricKeys = metrics.map(m => m.key);
  const titleCls = 'text-[13px] text-text-muted uppercase tracking-[0.08em] mb-2';

  if (mode === 'all') {
    const stats = buildAllTimeStats(forecast, metricKeys);
    const rows = metrics.map((m) => {
      const s = stats[m.key];
      return {
        id: m.key,
        metric: m.label,
        total: s?.total,
        mean: s?.mean,
        min: s?.min,
        max: s?.max,
        first: s?.first,
        last: s?.last,
      };
    });
    const moneyFmt = (v) => typeof v === 'number' ? <MoneyCell value={v} decimals={2} /> : '—';
    return (
      <div className="my-3">
        <h4 className={titleCls}>{title} — all-time ({forecast.length} months)</h4>
        <DataTable
          columns={[
            { key: 'metric', label: 'Metric', width: '24%' },
            { key: 'total',  label: 'Total',  align: 'right', format: moneyFmt },
            { key: 'mean',   label: 'Mean',   align: 'right', format: moneyFmt },
            { key: 'min',    label: 'Min',    align: 'right', format: moneyFmt },
            { key: 'max',    label: 'Max',    align: 'right', format: moneyFmt },
            { key: 'first',  label: 'First',  align: 'right', format: moneyFmt },
            { key: 'last',   label: 'Last',   align: 'right', format: moneyFmt },
          ]}
          rows={rows}
          compact
        />
      </div>
    );
  }

  if (mode === 'hybrid') {
    const momRows = buildComparison(forecast, 'mom', metricKeys);
    const qoqRows = buildComparison(forecast, 'qoq', metricKeys);
    const yoyRows = buildComparison(forecast, 'yoy', metricKeys);
    const rows = forecast.map((r, i) => ({
      id: r.ym,
      label: r.label,
      ...metrics.reduce((acc, m) => {
        acc[`${m.key}_mom`] = momRows[i].cells[m.key];
        acc[`${m.key}_qoq`] = qoqRows[i].cells[m.key];
        acc[`${m.key}_yoy`] = yoyRows[i].cells[m.key];
        acc[`${m.key}_val`] = Number(r[m.key] ?? 0);
        return acc;
      }, {}),
    }));
    return (
      <div className="my-3">
        <h4 className={titleCls}>{title} — hybrid (MoM · QoQ · YoY)</h4>
        <div className="grid grid-cols-1 gap-[10px]">
          {metrics.map((m) => (
            <DataTable
              key={m.key}
              columns={[
                { key: 'label', label: m.label, width: '18%' },
                { key: `${m.key}_val`, label: 'Value', align: 'right',
                  format: (v) => typeof v === 'number' ? <MoneyCell value={v} decimals={2} /> : '—' },
                { key: `${m.key}_mom`, label: 'MoM', align: 'right',
                  format: (v) => <DeltaCell cell={v} /> },
                { key: `${m.key}_qoq`, label: 'QoQ', align: 'right',
                  format: (v) => <DeltaCell cell={v} /> },
                { key: `${m.key}_yoy`, label: 'YoY', align: 'right',
                  format: (v) => <DeltaCell cell={v} /> },
              ]}
              rows={rows}
              compact
            />
          ))}
        </div>
      </div>
    );
  }

  /* Single-mode: mom | qoq | yoy */
  const cmpRows = buildComparison(forecast, mode, metricKeys);
  const rows = forecast.map((r, i) => {
    const c = cmpRows[i];
    return {
      id: r.ym,
      label: r.label,
      ...metrics.reduce((acc, m) => {
        acc[`${m.key}_val`] = Number(r[m.key] ?? 0);
        acc[`${m.key}_d`]   = c.cells[m.key];
        return acc;
      }, {}),
    };
  });
  const columns = [{ key: 'label', label: 'Period', width: '10%' }];
  metrics.forEach((m) => {
    columns.push({
      key: `${m.key}_val`, label: m.label, align: 'right',
      format: (v) => typeof v === 'number' ? <MoneyCell value={v} decimals={2} /> : '—',
    });
    columns.push({
      key: `${m.key}_d`, label: `Δ ${mode.toUpperCase()}`, align: 'right',
      format: (v) => <DeltaCell cell={v} />,
    });
  });

  const modeLabel = { mom: 'Month-on-Month', qoq: 'Quarter-on-Quarter', yoy: 'Year-on-Year' }[mode] || mode;
  return (
    <div className="my-3">
      <h4 className={titleCls}>{title} — {modeLabel}</h4>
      <DataTable columns={columns} rows={rows} compact />
    </div>
  );
}
